import { useCallback, useEffect, useMemo, useState } from "react";

import {
  DEFAULT_POSITIONS_WIDGET_VISIBLE_COLUMN_IDS,
  getPositionsWidgetAvailableColumnIds,
  isPositionsWidgetColumnId,
  type PositionsWidgetColumnId,
} from "./PositionsWidget.columns";

// v7: compact set plus % of portfolio.
export const POSITIONS_COLUMNS_STORAGE_KEY = "atd:positions:columns:v7";
export const POSITIONS_COLUMNS_WITH_ACTIONS_STORAGE_KEY =
  "atd:positions:columns:v7:actions";

export interface PositionsColumnVisibilityStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
}

const getDefaultColumnIds = (
  includeActions: boolean = false,
): PositionsWidgetColumnId[] =>
  includeActions
    ? [...DEFAULT_POSITIONS_WIDGET_VISIBLE_COLUMN_IDS]
    : DEFAULT_POSITIONS_WIDGET_VISIBLE_COLUMN_IDS.filter(
        (id) => id !== "actions",
      );

const getStorageKey = (includeActions: boolean): string =>
  includeActions
    ? POSITIONS_COLUMNS_WITH_ACTIONS_STORAGE_KEY
    : POSITIONS_COLUMNS_STORAGE_KEY;

/** Symbol (and Actions when shown) cannot be hidden. */
const isRequiredColumn = (
  columnId: PositionsWidgetColumnId,
  includeActions: boolean,
): boolean =>
  columnId === "symbol" || (includeActions && columnId === "actions");

const getPositionsColumnStorage =
  (): PositionsColumnVisibilityStorage | null => {
    if (typeof window === "undefined") {
      return null;
    }

    try {
      return window.localStorage;
    } catch {
      return null;
    }
  };

const sameColumnIds = (
  left: readonly PositionsWidgetColumnId[],
  right: readonly PositionsWidgetColumnId[],
): boolean =>
  left.length === right.length &&
  left.every((id, index) => id === right[index]);

export const clampPositionsColumnVisibility = (
  visibleColumnIds: readonly PositionsWidgetColumnId[],
  availableColumnIds: readonly PositionsWidgetColumnId[],
  includeActionsByDefault: boolean = false,
): PositionsWidgetColumnId[] => {
  const availableSet = new Set(availableColumnIds);
  const clamped = visibleColumnIds.filter((id) => availableSet.has(id));
  const withRequired = availableColumnIds.filter(
    (columnId) =>
      clamped.includes(columnId) ||
      isRequiredColumn(columnId, includeActionsByDefault),
  );

  if (withRequired.length > 0) {
    return withRequired;
  }

  return getDefaultColumnIds(includeActionsByDefault).filter((id) =>
    availableSet.has(id),
  );
};

export const sanitizePositionsColumnVisibility = (
  value: unknown,
  includeActionsByDefault: boolean = false,
): PositionsWidgetColumnId[] => {
  const availableColumnIds = getPositionsWidgetAvailableColumnIds(
    includeActionsByDefault,
  );

  if (!Array.isArray(value)) {
    return clampPositionsColumnVisibility(
      getDefaultColumnIds(includeActionsByDefault),
      availableColumnIds,
      includeActionsByDefault,
    );
  }

  const requestedColumnIds = new Set<PositionsWidgetColumnId>();
  for (const candidate of value) {
    if (isPositionsWidgetColumnId(candidate)) {
      requestedColumnIds.add(candidate);
    }
  }

  const sanitizedColumnIds = availableColumnIds.filter(
    (columnId) =>
      requestedColumnIds.has(columnId) ||
      isRequiredColumn(columnId, includeActionsByDefault),
  );

  return sanitizedColumnIds.length > 0
    ? sanitizedColumnIds
    : clampPositionsColumnVisibility(
        getDefaultColumnIds(includeActionsByDefault),
        availableColumnIds,
        includeActionsByDefault,
      );
};

export const loadPositionsColumnVisibility = (
  storage: PositionsColumnVisibilityStorage | null = getPositionsColumnStorage(),
  includeActionsByDefault: boolean = false,
): PositionsWidgetColumnId[] => {
  if (!storage) {
    return sanitizePositionsColumnVisibility(null, includeActionsByDefault);
  }

  try {
    const storedValue = storage.getItem(getStorageKey(includeActionsByDefault));
    return storedValue === null
      ? sanitizePositionsColumnVisibility(null, includeActionsByDefault)
      : sanitizePositionsColumnVisibility(
          JSON.parse(storedValue),
          includeActionsByDefault,
        );
  } catch {
    return sanitizePositionsColumnVisibility(null, includeActionsByDefault);
  }
};

export const persistPositionsColumnVisibility = (
  visibleColumnIds: readonly PositionsWidgetColumnId[],
  storage: PositionsColumnVisibilityStorage | null = getPositionsColumnStorage(),
  includeActionsByDefault: boolean = false,
): boolean => {
  if (!storage) {
    return false;
  }

  try {
    storage.setItem(
      getStorageKey(includeActionsByDefault),
      JSON.stringify(
        sanitizePositionsColumnVisibility(
          visibleColumnIds,
          includeActionsByDefault,
        ),
      ),
    );
    return true;
  } catch {
    return false;
  }
};

export const usePositionsColumnVisibility = (
  storage: PositionsColumnVisibilityStorage | null = getPositionsColumnStorage(),
  includeActionsByDefault: boolean = false,
) => {
  const availableColumnIds = useMemo(
    () => getPositionsWidgetAvailableColumnIds(includeActionsByDefault),
    [includeActionsByDefault],
  );

  const [visibleColumnIds, setVisibleColumnIds] = useState<
    PositionsWidgetColumnId[]
  >(() => loadPositionsColumnVisibility(storage, includeActionsByDefault));

  const effectiveVisibleColumnIds = useMemo(
    () =>
      clampPositionsColumnVisibility(
        visibleColumnIds,
        availableColumnIds,
        includeActionsByDefault,
      ),
    [availableColumnIds, includeActionsByDefault, visibleColumnIds],
  );

  useEffect(() => {
    setVisibleColumnIds((current) => {
      const next = clampPositionsColumnVisibility(
        current,
        availableColumnIds,
        includeActionsByDefault,
      );
      return sameColumnIds(current, next) ? current : next;
    });
  }, [availableColumnIds, includeActionsByDefault]);

  useEffect(() => {
    persistPositionsColumnVisibility(
      effectiveVisibleColumnIds,
      storage,
      includeActionsByDefault,
    );
  }, [effectiveVisibleColumnIds, includeActionsByDefault, storage]);

  const setColumnVisibility = useCallback(
    (columnId: PositionsWidgetColumnId, isVisible: boolean) => {
      if (!availableColumnIds.includes(columnId)) {
        return;
      }

      setVisibleColumnIds((currentColumnIds) => {
        const currentVisible = clampPositionsColumnVisibility(
          currentColumnIds,
          availableColumnIds,
          includeActionsByDefault,
        );
        const isCurrentlyVisible = currentVisible.includes(columnId);
        if (isCurrentlyVisible === isVisible) {
          return currentColumnIds;
        }
        if (!isVisible && isRequiredColumn(columnId, includeActionsByDefault)) {
          return currentColumnIds;
        }
        if (!isVisible && currentVisible.length === 1) {
          return currentColumnIds;
        }

        const nextColumnIds = new Set(currentVisible);
        if (isVisible) {
          nextColumnIds.add(columnId);
        } else {
          nextColumnIds.delete(columnId);
        }

        return availableColumnIds.filter((id) => nextColumnIds.has(id));
      });
    },
    [availableColumnIds, includeActionsByDefault],
  );

  const selectAllColumns = useCallback(() => {
    setVisibleColumnIds([...availableColumnIds]);
  }, [availableColumnIds]);

  const resetColumnVisibility = useCallback(() => {
    setVisibleColumnIds(
      clampPositionsColumnVisibility(
        getDefaultColumnIds(includeActionsByDefault),
        availableColumnIds,
        includeActionsByDefault,
      ),
    );
  }, [availableColumnIds, includeActionsByDefault]);

  const applyVisibleColumnIds = useCallback(
    (columnIds: readonly PositionsWidgetColumnId[]) => {
      setVisibleColumnIds(
        clampPositionsColumnVisibility(
          columnIds,
          availableColumnIds,
          includeActionsByDefault,
        ),
      );
    },
    [availableColumnIds, includeActionsByDefault],
  );

  return {
    visibleColumnIds: effectiveVisibleColumnIds,
    setColumnVisibility,
    selectAllColumns,
    resetColumnVisibility,
    setVisibleColumnIds: applyVisibleColumnIds,
  };
};
